import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { toast } from 'react-hot-toast';
import tenantConfigService, {
  Tag,
  TagSpec,
  TenantConfiguration,
} from '../../../services/tenant-config-service';
import { useTenantStore } from '../../../store/use-tenant-store';
import { useUserStore } from '../../../store/use-user-store';

// Query keys for caching and management
export const tenantConfigKeys = {
  tenantConfig: (configId: number) => ['tenant-config', configId],
  tagSpecs: ['tag-specs'],
  tags: ['tags'],
} as const;

/**
 * The config endpoint lives under org-administration and returns 403 for
 * learners, so only admins request it. Everyone else uses the default tabs.
 */
export const useCanReadTenantConfig = () =>
  useUserStore(
    (state) => state.hasAdminPermissions || state.hasSuperAdminPermissions
  );

/** Client errors (403 forbidden, 404 no config) won't change on retry. */
export const retryTenantConfig = (failureCount: number, error: unknown) => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < 3;
};

/**
 * Hook to fetch tenant configuration. Resolves to null when the tenant has none.
 */
export const useTenantConfig = (configId: number) => {
  const setConfig = useTenantStore((state) => state.setConfig);
  const canReadTenantConfig = useCanReadTenantConfig();

  const query = useQuery({
    queryKey: tenantConfigKeys.tenantConfig(configId),
    queryFn: async (): Promise<TenantConfiguration | null> => {
      try {
        return await tenantConfigService.getTenantConfig(configId);
      } catch (error) {
        if (
          (error as { response?: { status?: number } })?.response?.status ===
          404
        ) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!configId && canReadTenantConfig,
    retry: retryTenantConfig,
  });

  // The stored config is persisted and read app-wide; drop it when it belongs
  // to another tenant (switch, reload on another tenant, logout) so it never
  // leaks across tenants, including for users who can't refetch it.
  React.useEffect(() => {
    const { config, reset } = useTenantStore.getState();
    if (config && config.tenant !== configId) {
      reset();
    }
  }, [configId]);

  // Save to zustand store once fetched (null when the tenant has no config)
  React.useEffect(() => {
    if (query.isSuccess) {
      setConfig(query.data);
    }
  }, [query.isSuccess, query.data, setConfig]);

  return query;
};

/**
 * Hook to create tenant configuration
 */
export const useCreateTenantConfig = () => {
  const queryClient = useQueryClient();
  const setConfig = useTenantStore((state) => state.setConfig);

  return useMutation({
    mutationFn: (
      data: Omit<TenantConfiguration, 'id' | 'created_at' | 'updated_at'>
    ) => tenantConfigService.createTenantConfig(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: tenantConfigKeys.tenantConfig(data.id!),
      });
      // Save to zustand store
      setConfig(data);
      toast.success('Tenant configuration created successfully');
    },
    onError: (error: any) => {
      console.error('Error creating tenant config:', error);
      const errorMessage =
        error?.response?.data?.dashboard_config?.[0] ||
        error?.response?.data?.detail ||
        'Failed to create tenant configuration';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to update tenant configuration (full)
 */
export const useUpdateTenantConfig = (configId: number) => {
  const queryClient = useQueryClient();
  const setConfig = useTenantStore((state) => state.setConfig);

  return useMutation({
    mutationFn: (
      data: Omit<
        TenantConfiguration,
        'id' | 'tenant' | 'created_at' | 'updated_at'
      >
    ) => tenantConfigService.updateTenantConfig(configId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: tenantConfigKeys.tenantConfig(configId),
      });
      // Save to zustand store
      setConfig(data);
      toast.success('Tenant configuration updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating tenant config:', error);
      const errorMessage =
        error?.response?.data?.dashboard_config?.[0] ||
        error?.response?.data?.detail ||
        'Failed to update tenant configuration';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to update tenant configuration (partial)
 */
export const usePatchTenantConfig = (configId: number) => {
  const queryClient = useQueryClient();
  const setConfig = useTenantStore((state) => state.setConfig);

  return useMutation({
    mutationFn: (
      data: Partial<
        Omit<TenantConfiguration, 'id' | 'tenant' | 'created_at' | 'updated_at'>
      >
    ) => tenantConfigService.patchTenantConfig(configId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: tenantConfigKeys.tenantConfig(configId),
      });
      // Save to zustand store
      setConfig(data);
      toast.success('Tenant configuration updated successfully');
    },
    onError: (error: any) => {
      console.error('Error patching tenant config:', error);
      const errorMessage =
        error?.response?.data?.dashboard_config?.[0] ||
        error?.response?.data?.detail ||
        'Failed to update tenant configuration';
      toast.error(errorMessage);
    },
  });
};

/**
 * Hook to fetch tag specifications
 */
export const useTagSpecs = () => {
  return useQuery({
    queryKey: tenantConfigKeys.tagSpecs,
    queryFn: () => tenantConfigService.getTagSpecs(),
  });
};

/**
 * Hook to create tag specification
 */
export const useCreateTagSpec = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<TagSpec, 'id' | 'created_at'>) =>
      tenantConfigService.createTagSpec(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.tagSpecs });
      toast.success('Tag specification created successfully');
    },
    onError: (error: any) => {
      console.error('Error creating tag spec:', error);
      toast.error('Failed to create tag specification');
    },
  });
};

/**
 * Hook to update tag specification
 */
export const useUpdateTagSpec = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TagSpec> }) =>
      tenantConfigService.updateTagSpec(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.tagSpecs });
      toast.success('Tag specification updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating tag spec:', error);
      toast.error('Failed to update tag specification');
    },
  });
};

/**
 * Hook to fetch tags
 */
export const useTags = () => {
  return useQuery({
    queryKey: tenantConfigKeys.tags,
    queryFn: () => tenantConfigService.getTags(),
  });
};

/**
 * Hook to create tag
 */
export const useCreateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Omit<Tag, 'id' | 'created_at'>) =>
      tenantConfigService.createTag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.tags });
      toast.success('Tag created successfully');
    },
    onError: (error: any) => {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    },
  });
};

/**
 * Hook to update tag
 */
export const useUpdateTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Tag> }) =>
      tenantConfigService.updateTag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.tags });
      toast.success('Tag updated successfully');
    },
    onError: (error: any) => {
      console.error('Error updating tag:', error);
      toast.error('Failed to update tag');
    },
  });
};

/**
 * Hook to delete tag
 */
export const useDeleteTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => tenantConfigService.deleteTag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantConfigKeys.tags });
      toast.success('Tag deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting tag:', error);
      toast.error('Failed to delete tag');
    },
  });
};

/**
 * Hook to get available tag values from tag specs for dropdown
 */
export const useAvailableTagValues = () => {
  const { data: tagSpecs } = useTagSpecs();
  const { data: tags } = useTags();

  // Get all allowed values from tag specs
  const availableValues =
    tagSpecs?.results?.[0]?.constraints?.allowed_values || [];

  // Get existing tags to show what's already been created
  const existingTags = tags?.results || [];

  return {
    availableValues,
    existingTags,
    hasTagSpec: !!tagSpecs?.results?.length,
  };
};
